import { flatMap } from "lodash";
import uniqBy from "lodash/uniqBy";
import { useInfiniteQuery } from "react-query";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { Comment } from "@circle-react/types/Comment";

interface UseCommentsInfiniteQueryParams {
  postId: string | number;
  parentCommentId: string | number | null;
  usedIn: string;
  preloaded?: boolean;
  preloadedComments?: Comment[];
  preloadedCommentsTotalCount?: number;
  onCommentsUpdate: (comments: Comment[]) => void;
}

export const useCommentsInfiniteQuery = ({
  postId,
  parentCommentId,
  usedIn,
  preloaded = false,
  preloadedComments = [],
  preloadedCommentsTotalCount = 0,
  onCommentsUpdate,
}: UseCommentsInfiniteQueryParams) =>
  useInfiniteQuery(
    [usedIn, postId, "comments", "list", parentCommentId].filter(Boolean),
    ({ pageParam = 1 }) => {
      const pageParams = { per_page: 15, page: pageParam };
      const params = parentCommentId
        ? { parent_comment_id: parentCommentId, ...pageParams }
        : pageParams;

      return reactQueryGet(
        internalApi.comments.index({
          postId: postId,
          params,
        }),
      );
    },
    {
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      onSuccess: ({ pages }) => {
        onCommentsUpdate(
          uniqBy(
            flatMap(pages, page => page.records),
            "id",
          ),
        );
      },
      initialData: () => {
        if (preloaded) {
          return {
            pages: [
              {
                records: preloadedComments,
                page: 0,
                has_next_page:
                  preloadedCommentsTotalCount > preloadedComments.length,
              },
            ],
            pageParams: [],
          };
        }
        return undefined;
      },
      cacheTime: 0,
      staleTime: Infinity,
      notifyOnChangeProps: "tracked",
    },
  );
